import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

import { isValidCnpj, unformatCnpj } from '../../core/utils/cnpj.util';

/**
 * Validadores customizados para formulários
 */
export class CustomValidators {

  /**
   * Valida campo obrigatório que pode conter false.
   */
  static requiredValue(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (value === null || value === undefined || value === '') {
      return { requiredValue: true };
    }

    return null;
  }

  /**
   * Valida CNPJ (numérico legado ou alfanumérico) via IN RFB 2.229/2024
   */
  static cnpj(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (value == null || value === '') {
      return null;
    }

    const cnpj = unformatCnpj(value);

    if (!cnpj) {
      return { invalidCNPJ: true };
    }

    if (!isValidCnpj(cnpj)) {
      return { invalidCNPJ: true };
    }

    return null;
  }

  /**
   * Valida número de telefone brasileiro
   */
  static phone(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const phone = value?.replace(/\D/g, '');

    if (!phone) {
      if (value) {
        return { invalidPhone: true };
      }
      return null;
    }

    if (phone.length < 10 || phone.length > 11) {
      return { invalidPhone: true };
    }

    return null;
  }

  /**
   * Valida CEP brasileiro
   */
  static cep(control: AbstractControl): ValidationErrors | null {
    const cep = control.value?.replace(/\D/g, '');
    if (!cep) return null;

    if (cep.length !== 8) {
      return { invalidCEP: true };
    }

    return null;
  }

  /**
   * Valida força da senha
   */
  static passwordStrength(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
    const valid = regex.test(value);
    return valid ? null : { invalidPassword: true };
  }

  /**
   * Valida nome completo (pelo menos 2 palavras)
   */
  static fullName(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) return null;

    // Remove espaços extras e divide em palavras
    const words = value.split(/\s+/).filter((word: string) => word.length > 0);

    if (words.length < 2) {
      return { invalidFullName: true };
    }

    // Verifica se cada palavra tem pelo menos 2 caracteres (evita iniciais soltas)
    const hasValidWords = words.every((word: string) => word.length >= 2);
    if (!hasValidWords) {
      return { invalidFullName: true };
    }

    return null;
  }

  /**
   * Valida se as senhas coincidem
   */
  static passwordMatch: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const form = control as FormGroup;
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ mismatch: true });
    } else if (confirmPassword) {
      confirmPassword.setErrors(null);
    }
    return null;
  };

  /**
   * Valida email corporativo (não permite domínios pessoais)
   */
  static corporateEmail(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const allowedDomains = [
      '@globalhitss.com.br',
      '@claro.com.br',
      '@brasilcenter.com.br'
    ];

    const email = value.toLowerCase();
    const isValidDomain = allowedDomains.some(domain => email.endsWith(domain));

    if (!isValidDomain) {
      return { corporateEmail: true };
    }

    return null;
  }

  /**
   * Valida email sem espaços
   */
  static emailNoSpaces(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    if (value.includes(' ')) {
      return { emailNoSpaces: true };
    }

    return null;
  }

  /**
   * Valida que o campo não contém apenas espaços em branco
   */
  static noWhitespaceOnly(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    if (typeof value === 'string' && value.trim().length === 0) {
      return { noWhitespaceOnly: true };
    }

    return null;
  }

  static noLeadingWhitespace(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;

  if (typeof value === 'string' && value.length > 0 && value[0] === ' ') {
    return { leadingWhitespace: true };
  }

  return null;
}

  /**
   * Valida número de endereço: alfanumérico com pelo menos 1 dígito (ex: 32A, 4325B)
   */
  static addressNumber(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || typeof value !== 'string') return null;

    if (!/\d/.test(value)) {
      return { addressNumberNoDigit: true };
    }

    return null;
  }

  static emailWithDomainValidator(): ValidatorFn {
    const baseEmailRe = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+$/;
    const allowedTlds = ['.com', '.com.br', '.org', '.org.br', '.net'];
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const email = String(value).trim().toLowerCase();
      if (!baseEmailRe.test(email)) {
        return { invalidEmailDomain: true };
      }

      const hasAllowedTld =
        email.endsWith('.br') || allowedTlds.some((tld) => email.endsWith(tld));
      return hasAllowedTld ? null : { invalidEmailDomain: true };
    };
  }
}